import React, { useState } from "react";
import { Sparkles } from "lucide-react";
import { GROUPS } from "@/lib/reteForm";

const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-");
const inputCls = "w-full rounded-xl border border-viola/20 bg-white px-4 py-3 text-base text-viola focus:outline-none focus:ring-2 focus:ring-fucsia dark:bg-viola/40 dark:border-cream/20 dark:text-cream";

export function Field({ id, label, hint, required, error, children }) {
  return (
    <div className="min-w-0" data-testid={`field-${id}`}>
      <label htmlFor={id} className="block font-semibold">
        {label} {required && <span className="text-fucsia" aria-hidden>*</span>}
        {required && <span className="sr-only">(obbligatorio)</span>}
      </label>
      {hint && <p id={`${id}-hint`} className="mt-1 text-sm text-viola/70 dark:text-cream/70">{hint}</p>}
      <div className="mt-2">{children}</div>
      {error && <p id={`${id}-error`} className="mt-1.5 text-sm font-semibold text-red-700 dark:text-red-300" role="alert">{error}</p>}
    </div>
  );
}

export function TextInput({ id, value, onChange, error, hint, multiline, ...rest }) {
  const props = {
    id, value: value || "", onChange: (e) => onChange(e.target.value), className: inputCls,
    "aria-invalid": !!error, "aria-describedby": [hint && `${id}-hint`, error && `${id}-error`].filter(Boolean).join(" ") || undefined,
    "data-testid": `input-${id}`, ...rest,
  };
  return multiline ? <textarea rows={4} {...props} /> : <input type="text" {...props} />;
}

function Option({ type, name, checked, onChange, label, testId }) {
  return (
    <label className={`flex items-start gap-3 rounded-xl border px-3 py-2.5 cursor-pointer transition-colors ${checked ? "border-viola bg-lilla/50 dark:border-cream dark:bg-cream/10" : "border-viola/15 hover:border-viola/40 dark:border-cream/15"}`}>
      <input type={type} name={name} checked={checked} onChange={onChange} className="mt-1 h-4 w-4 accent-fucsia shrink-0" data-testid={testId} />
      <span className="text-sm">{label}</span>
    </label>
  );
}

export function ChoiceGroup({ id, legend, hint, required, error, options, value, onChange, multiple, allowOther }) {
  const selected = multiple ? value : [value];
  const others = multiple ? value.filter((v) => !options.includes(v)) : [];
  const [other, setOther] = useState(others.join(", "));
  const toggle = (o) => (multiple ? onChange(value.includes(o) ? value.filter((v) => v !== o) : [...value, o]) : onChange(o));
  const setOtherText = (t) => {
    setOther(t);
    const extra = t.split(",").map((x) => x.trim()).filter(Boolean);
    onChange([...value.filter((v) => options.includes(v)), ...extra]);
  };
  return (
    <fieldset className="min-w-0" data-testid={`field-${id}`} aria-describedby={error ? `${id}-error` : undefined}>
      <legend className="font-semibold">
        {legend} {required && <span className="text-fucsia" aria-hidden>*</span>}
        {required && <span className="sr-only">(obbligatorio)</span>}
      </legend>
      {hint && <p className="mt-1 text-sm text-viola/70 dark:text-cream/70">{hint}</p>}
      <div className="mt-3 grid sm:grid-cols-2 gap-2">
        {options.map((o) => (
          <Option key={o} type={multiple ? "checkbox" : "radio"} name={id} checked={selected.includes(o)} onChange={() => toggle(o)} label={o} testId={`opt-${id}-${slug(o)}`} />
        ))}
      </div>
      {allowOther && (
        <input type="text" value={other} onChange={(e) => setOtherText(e.target.value)} placeholder="Altro (separa con una virgola)" aria-label={`${legend}: altro`} className={`${inputCls} mt-2 text-sm`} data-testid={`input-${id}-altro`} />
      )}
      {error && <p id={`${id}-error`} className="mt-1.5 text-sm font-semibold text-red-700 dark:text-red-300" role="alert">{error}</p>}
    </fieldset>
  );
}

function NewBadge() {
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-giallo text-viola px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest">
      <Sparkles size={10} aria-hidden /> Voce nuova
    </span>
  );
}

export function GroupGrid({ id, legend, hint, error, levels, value, onChange, highlightNew }) {
  return (
    <fieldset className="min-w-0" data-testid={`field-${id}`} aria-describedby={error ? `${id}-error` : undefined}>
      <legend className="font-semibold">{legend} <span className="text-fucsia" aria-hidden>*</span><span className="sr-only">(obbligatorio)</span></legend>
      {hint && <div className="mt-1 text-sm text-viola/70 dark:text-cream/70 space-y-2">{hint}</div>}
      <div className="mt-4 space-y-2">
        {GROUPS.map((g) => {
          const isNew = g.isNew;
          return (
            <div key={g.key} role="radiogroup" aria-label={`${g.label}${isNew ? " (voce nuova, facoltativa)" : ""}`}
              className={`rounded-xl border p-3 sm:flex sm:items-center sm:justify-between gap-3 ${isNew && highlightNew ? "border-giallo bg-giallo/20 ring-2 ring-giallo" : isNew ? "border-giallo/70 bg-giallo/10" : "border-viola/10 dark:border-cream/10"}`}
              data-testid={`grid-${id}-${slug(g.key)}`}>
              <div className="text-sm font-medium flex flex-wrap items-center gap-2">
                {g.label} {isNew && <NewBadge />}
                {isNew && <span className="w-full text-xs font-normal text-viola/70 dark:text-cream/70">Questa voce prima mancava nel modulo. Facoltativa, ma molto utile.</span>}
              </div>
              <div className="mt-2 sm:mt-0 flex flex-wrap gap-1.5 shrink-0">
                {levels.map((l) => {
                  const v = typeof l === "object" ? l.value : l;
                  const lab = typeof l === "object" ? l.label : l;
                  const checked = value[g.key] === v;
                  return (
                    <label key={lab} className={`cursor-pointer rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors focus-within:ring-2 focus-within:ring-fucsia ${checked ? "bg-viola text-cream border-viola dark:bg-fucsia dark:border-fucsia" : "border-viola/20 hover:border-viola/50 dark:border-cream/20"}`}>
                      <input type="radio" className="sr-only" name={`${id}-${g.key}`} checked={checked} onChange={() => onChange({ ...value, [g.key]: v })} data-testid={`grid-${id}-${slug(g.key)}-${slug(lab)}`} />
                      {lab}
                    </label>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
      {error && <p id={`${id}-error`} className="mt-1.5 text-sm font-semibold text-red-700 dark:text-red-300" role="alert">{error}</p>}
    </fieldset>
  );
}
